import { useState, useMemo, useEffect } from "react";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import ButtonComponent from "../../../components/common/button/Button";
import FilterMultiSelectExtractSAP from "../../../components/common/FilterMultiSelectExtractSAP";
import {
  getForecastHistoryFilterDataEndpoint,
  getForecastHistoryExtractionDatesEndpoint,
  getBuyerForecastHistoryExtractionDatesEndpoint,
  getBuyerForecastHistoryFilterDropdownEndpoint,
  postBuyerForecastHistoryExportEndpoint,
  postForecastHistoryDownloadRequestEndpoint,
  getSupplierFilterDropdownEndpoint,
  postSupplierForecastHistoryExportEndpoint,
  getSupplierForecastHistoryFilterDropdownEndpoint,
} from "../../../common/endpoints";
import dayjs from "dayjs";
import { getForecastHistoryFilterData } from "../../../redux/actions/getForecastHistoryFilterDataSlice";

import { postForecastHistoryDownloadRequest } from "../../../redux/actions/postForecastHistoryDownloadSlice";
import MonthDateDropdown from "../../../components/common/MonthDateDropdown";

// ─── helpers ─────────────────────────────────────────────────────────────────

const isSupplier = () => {
  const role = localStorage.getItem("userRole");
  return role === "Supplier Planner" || role === "Supplier Admin";
};

const getUniqueOptions = (data, config) => {
  const uniqueMap = new Map();
  data.forEach((row, index) => {
    if (config.codeField && config.nameField) {
      const code = row[config.codeField];
      const name = row[config.nameField];
      const extra = config.extraField ? row[config.extraField] : null;
      const isEmpty = (v) => v === null || v === undefined || v === "";
      if (isEmpty(code) && isEmpty(name) && isEmpty(extra)) return;
      const parts = [];
      if (!config.nameOnly && code) parts.push(code);
      if (name) parts.push(name);
      if (extra) parts.push(extra);
      const label = parts.join(" | ");
      const uniqueKey = code || `${name}_${extra}_${index}`;
      uniqueMap.set(uniqueKey, { label, value: uniqueKey, actualValue: code });
    } else {
      const value = row[config.key];
      if (value == null || value === "") return;
      uniqueMap.set(value, { label: value, value });
    }
  });
  return [...uniqueMap.values()];
};

const hasSelectedDate = (value) => {
  if (typeof value === "string") return value.trim().length > 0;
  return Boolean(value);
};

// ─── filter configs ───────────────────────────────────────────────────────────

const BUYER_FILTER_CONFIG = [
  { key: "plant", label: "Plant" },
  { key: "parentVendor", label: "Parent Vendor", codeField: "parentVendor", nameField: "parentVendorName", nameOnly: true },
  { key: "childVendor", label: "Child Vendor", codeField: "childVendor", nameField: "childVendorName" },
  { key: "material", label: "Material" },
  { key: "externalMaterialGroup", label: "External Material Group" },
  { key: "businessCategory", label: "Business Category" },
  { key: "purchasingGroupCode", label: "Purchase Group", codeField: "purchasingGroupCode", nameField: "purchasingGroupName" },
  { key: "mrpControllerCode", label: "MRP Controller", codeField: "mrpControllerCode", nameField: "mrpControllerName" },
  { key: "gmpCode", label: "GMP", codeField: "gmpCode", nameField: "gmpName" },
];

const SUPPLIER_FILTER_CONFIG = [
  { key: "plant", label: "Plant", codeField: "plant", nameField: "plantName" },
  { key: "parentVendorName", label: "Parent Vendor" , codeField: "parentVendor", nameField: "parentVendorName" },
  { key: "childVendor", label: "Child Vendor", codeField: "childVendor", nameField: "childVendorName" },
  { key: "material", label: "Material" },
  { key: "supplierPin", label: "Supplier PIN" },
  { key: "businessCategory", label: "Business Category" },
];

// ─── Section Header ───────────────────────────────────────────────────────────

const SectionHeader = ({ title, icon, rightContent }) => (
  <Box
    sx={{
      backgroundColor: "#f5f5f5",
      border: "1px solid #d9d9d9",
      px: 2,
      py: 1,
      mt: 2,
      display: "flex",
      alignItems: "center",
      gap: 1,
    }}
  >
    {icon && icon}
    <Typography fontSize={14} fontWeight={600}>
      {title}
    </Typography>
    <Box sx={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
      {rightContent}
    </Box>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const ForecastHistoryTab = ({ onDownloadRequested }) => {
  const dispatch = useDispatch();
  const supplierMode = isSupplier();
  const userEmail = localStorage.getItem("userEmail");
  const selectedRegion = useSelector((state) => state?.setRegionPlantStore?.selectedRegion);
  const selectedPlant = useSelector((state) => state?.setRegionPlantStore?.selectedPlant);
  const userRole = localStorage.getItem("userRole");
  const isRegionalPlanner = userRole === "Regional Planner" || userRole === "Regional Viewer";

  const filterConfig = supplierMode ? SUPPLIER_FILTER_CONFIG : BUYER_FILTER_CONFIG;

  // date dropdowns from API
  const [fromDateOptions, setFromDateOptions] = useState([]);
  const [toDateOptions, setToDateOptions] = useState([]);
  const [selectedFromDate, setSelectedFromDate] = useState("");
  const [selectedToDate, setSelectedToDate] = useState("");
  const [loadingDates, setLoadingDates] = useState(false);

  // filter data (for cross-filtering dropdown options)
  const [filterDropdownData, setFilterDropdownData] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);

  // selected filter values
  const initialFilters = useMemo(() => {
    const init = {};
    filterConfig.forEach((f) => { init[f.key] = []; });
    return init;
  }, [filterConfig]);
  const [selectedFilters, setSelectedFilters] = useState(initialFilters);

  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadRequestModalOpen, setDownloadRequestModalOpen] = useState(false);

  const isDateRangeSelected = useMemo(
    () => hasSelectedDate(selectedFromDate) && hasSelectedDate(selectedToDate),
    [selectedFromDate, selectedToDate]
  );

  const handleDownloadModalOk = () => {
    setDownloadRequestModalOpen(false);
    if (onDownloadRequested) onDownloadRequested();
  };

  // ── fetch extraction dates on mount ────────────────────────────────────────
  useEffect(() => {
    const fetchDates = async () => {
      setLoadingDates(true);
      try {
        const userEmail = localStorage.getItem("userEmail");
        const extractionDatesEndpoint = `${getBuyerForecastHistoryExtractionDatesEndpoint}/${encodeURIComponent(userEmail)}`;
        const res = await dispatch(
          getForecastHistoryFilterData({
            dynamicEndpoint: extractionDatesEndpoint,
            skipUserEmailParam: !supplierMode,
          })
        );
        if (res?.meta?.requestStatus === "fulfilled") {
          const dates = Array.isArray(res.payload)
            ? res.payload
            : res.payload?.cpsDpsExtractionDates || [];
          const opts = dates.map((date) => ({
            label: dayjs(date).format("DD-MMM-YYYY"), // 31 Jul 2026
            value: date, // Keep original value for API
          }));
          setFromDateOptions(opts);
          setToDateOptions(opts);
        }
      } catch (e) {
        console.error("Error fetching extraction dates", e);
      } finally {
        setLoadingDates(false);
      }
    };
    fetchDates();
  }, [dispatch, supplierMode]);

  // ── fetch buyer filter dropdown data on mount (non-supplier only) ──────────
  useEffect(() => {
    if (supplierMode) return;
    const fetchBuyerFilterData = async () => {
      setLoadingFilters(true);
      try {
        const userEmail = localStorage.getItem("userEmail");
        const params = {};
        if (isRegionalPlanner && selectedRegion && selectedPlant) {
          const plantCode = selectedPlant.split(" - ")[0];
          params.region = selectedRegion;
          params.plant = plantCode;
        }
        const res = await dispatch(
          getForecastHistoryFilterData({
            dynamicEndpoint: `${getBuyerForecastHistoryFilterDropdownEndpoint}/${encodeURIComponent(userEmail)}`,
            skipUserEmailParam: true,
            params,
          })
        );
        if (res?.meta?.requestStatus === "fulfilled") {
          setFilterDropdownData(res.payload || []);
        }
      } catch (e) {
        console.error("Error fetching buyer filter dropdown data", e);
      } finally {
        setLoadingFilters(false);
      }
    };
    fetchBuyerFilterData();
  }, [dispatch, supplierMode, isRegionalPlanner, selectedRegion, selectedPlant]);

  // ── fetch supplier filter dropdown data when date range changes ────────────
  useEffect(() => {
    if (!supplierMode) return;
    const fetchFilterData = async () => {
      setLoadingFilters(true);
      try {
        const params = {};
        if (selectedFromDate) params.fromDate = selectedFromDate;
        if (selectedToDate) params.toDate = selectedToDate;
        const res = await dispatch(
          getForecastHistoryFilterData({
            dynamicEndpoint: `${getSupplierForecastHistoryFilterDropdownEndpoint}/${encodeURIComponent(userEmail)}`,
            skipUserEmailParam: true,
            params,
          })
        );
        if (res?.meta?.requestStatus === "fulfilled") {
          setFilterDropdownData(res.payload || []);
        }
      } catch (e) {
        console.error("Error fetching filter data", e);
      } finally {
        setLoadingFilters(false);
      }
    };
    fetchFilterData();
    // reset filters when date changes
    setSelectedFilters(initialFilters);
  }, [selectedFromDate, selectedToDate, dispatch, initialFilters, supplierMode]);

  // ── cross-filtered dropdown options ────────────────────────────────────────
  const dropdownOptions = useMemo(() => {
    const result = {};
    filterConfig.forEach((filter) => {
      let tempData = [...filterDropdownData];
      filterConfig.forEach((config) => {
        if (config.key !== filter.key) {
          const selected = selectedFilters[config.key];
          if (selected && selected.length > 0) {
            const isAllSelected = selected.some((item) => item?.__isAll);
            if (!isAllSelected) {
              tempData = tempData.filter((row) =>
                selected.some((item) => {
                  const compareField = config.codeField || config.key;
                  return (item.actualValue ?? item.value) === row[compareField];
                })
              );
            }
          }
        }
      });
      result[filter.key] = getUniqueOptions(tempData, filter);
    });
    return result;
  }, [selectedFilters, filterDropdownData, filterConfig]);

  const handleFilterChange = (key, values) => {
    setSelectedFilters((prev) => ({ ...prev, [key]: values }));
  };

  // ── clear all filters ──────────────────────────────────────────────────────
  const handleClearAll = () => {
    setSelectedFilters(initialFilters);
    setSelectedFromDate("");
    setSelectedToDate("");
  };

  // ── request download ───────────────────────────────────────────────────────
  const handleDownloadRequest = async () => {
    const userEmail = localStorage.getItem("userEmail");
    if (!isDateRangeSelected) {
      toast.warn("Please select From Date and To Date before downloading", { autoClose: 2500 });
      return;
    }
    setDownloadLoading(true);
    try {
      let res;

      if (!supplierMode) {
        const params = {};

        if (isRegionalPlanner && selectedRegion && selectedPlant) {
          const plantCode = selectedPlant.split(" - ")[0];
          params.region = selectedRegion;
          params.plant = plantCode;
        }

        const selectedFilterValues = (key, fallback = []) => {
          const selected = selectedFilters[key];
          if (!Array.isArray(selected) || selected.length === 0) return fallback;
          return selected.map((item) => item.actualValue ?? item.value ?? "");
        };

        const requestBody = {
          fromExtractionDate: selectedFromDate,
          toExtractionDate: selectedToDate,
          plants: selectedFilterValues("plant"),
          parentVendors: selectedFilterValues("parentVendor"),
          childVendors: selectedFilterValues("childVendor", [""]),
          materials: selectedFilterValues("material", [""]),
          externalMaterialGroups: selectedFilterValues("externalMaterialGroup"),
          purchasingGroups: selectedFilterValues("purchasingGroupCode"),
          mrpControllers: selectedFilterValues("mrpControllerCode"),
          gmps: selectedFilterValues("gmpCode"),
          businessCategories: selectedFilterValues("businessCategory"),
        };

        res = await dispatch(
          postForecastHistoryDownloadRequest({
            dynamicEndpoint: `${postBuyerForecastHistoryExportEndpoint}/${encodeURIComponent(userEmail)}`,
            data: requestBody,
            skipUserEmailParam: true,
            params,
          })
        );
      } else {
        const selectedFilterValues = (key, fallback = []) => {
          const selected = selectedFilters[key];
          if (!Array.isArray(selected) || selected.length === 0) return fallback;

          return selected.map((item) => item.actualValue ?? item.value ?? "");
        };

        const requestBody = {
          fromPublishDate: selectedFromDate,
          toPublishDate: selectedToDate,
          plants: selectedFilterValues("plant"),
          parentVendors: selectedFilterValues("parentVendorName"),
          childVendors: selectedFilterValues("childVendor", [""]),
          materials: selectedFilterValues("material", [""]),
          supplierPin: selectedFilterValues("supplierPin"),
          businessCategories: selectedFilterValues("businessCategory"),
        };

        res = await dispatch(
          postForecastHistoryDownloadRequest({
            dynamicEndpoint: `${postSupplierForecastHistoryExportEndpoint}/${encodeURIComponent(userEmail)}`,
            skipUserEmailParam: true,
            data: requestBody,
          })
        );
      }

      if (res?.meta?.requestStatus === "fulfilled") {
        const downloadResponse = res?.payload?.data || res?.payload;
        const hasDownloadJobResponse = !!downloadResponse && typeof downloadResponse === "object" &&
          (Object.prototype.hasOwnProperty.call(downloadResponse, "jobId") ||
            Object.prototype.hasOwnProperty.call(downloadResponse, "status"));

        if (hasDownloadJobResponse) {
          setDownloadRequestModalOpen(true);
        } else {
          toast.success("Download request submitted. Check My Downloads tab.", { autoClose: 3000 });
        }
      } else {
        toast.error("Failed to submit download request", { autoClose: 2500 });
      }
    } catch (e) {
      console.error("Download request error", e);
      toast.error("Error submitting download request", { autoClose: 2500 });
    } finally {
      setDownloadLoading(false);
    }
  };

  const dateSectionLabel = supplierMode ? "Last Customer Update" : "Forecast Snapshot Date";

  return (
    <Box sx={{ pb: 4 }}>
      {/* ── Section 1: Date Range ─────────────────────────────────────── */}
      <SectionHeader title={dateSectionLabel} />
      <Box
        sx={{
          border: "1px solid #d9d9d9",
          p: 2,
          display: "flex",
          alignItems: "center",
          gap: 3,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography fontSize={13} sx={{ whiteSpace: "nowrap", minWidth: 80 }}>
            From Date
          </Typography>
          <MonthDateDropdown
            label={
              loadingDates ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <span>From Date</span>
                  <CircularProgress size={10} thickness={6} sx={{ color: "#000" }} />
                </Box>
              ) : (
                "From Date"
              )
            }
            value={selectedFromDate}
            options={fromDateOptions}
            handleChange={(e) => setSelectedFromDate(e.target.value)}
            disabled={loadingDates}
            maxWidth="200px"
            width="200px"
            height="36px"
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography fontSize={13} sx={{ whiteSpace: "nowrap", minWidth: 60 }}>
            To Date
          </Typography>
          <MonthDateDropdown
            label={
              loadingDates ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <span>To Date</span>
                  <CircularProgress size={10} thickness={6} sx={{ color: "#000" }} />
                </Box>
              ) : (
                "To Date"
              )
            }
            value={selectedToDate}
            options={toDateOptions}
            handleChange={(e) => setSelectedToDate(e.target.value)}
            disabled={loadingDates}
            maxWidth="200px"
            width="200px"
            height="36px"
          />
        </Box>
      </Box>

      {/* ── Section 2: Filters ────────────────────────────────────────── */}
      <SectionHeader
        title="Filters"
        icon={<FilterAltIcon sx={{ fontSize: 18 }} />}
      />
      <Box sx={{ border: "1px solid #d9d9d9", p: 2 }}>
        <Grid container spacing={2}>
          {filterConfig.map((filter) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={filter.key}>
              <FilterMultiSelectExtractSAP
                label={
                  loadingFilters ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <span>{filter.label}</span>
                      <CircularProgress size={12} thickness={6} sx={{ color: "#000" }} />
                    </Box>
                  ) : (
                    filter.label
                  )
                }
                disabled={loadingFilters}
                options={dropdownOptions[filter.key] || []}
                value={selectedFilters[filter.key] || []}
                onChange={(values) => handleFilterChange(filter.key, values)}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box sx={{ display: "flex", gap: 1.5, mt: 2, justifyContent: "flex-end" }}>
        <ButtonComponent
          type="primary"
          maxWidth="130px"
          label="Clear All"
          height="36px"
          onClick={handleClearAll}
        />
        <ButtonComponent
          type="secondary"
          maxWidth="180px"
          label="Request Download"
          height="36px"
          onClick={handleDownloadRequest}
          disabled={downloadLoading || !isDateRangeSelected}
        />
      </Box>

      <Dialog
        open={downloadRequestModalOpen}
        onClose={() => setDownloadRequestModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Download Request Submitted</DialogTitle>
        <DialogContent>
          <Typography fontSize={14}>
            {!supplierMode ? 'Your request is being processed. Once it is completed, you will be notified by email and you can download it from My Downloads.' :
            'Your request is being processed. Once it is completed, you will be notified by email, and you can download it from My Download. Processing may take up to 25 minutes.'
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleDownloadModalOk}
            sx={{
              textTransform: "none",
              backgroundColor: "#000",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#000",
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ForecastHistoryTab;
